import { areaKey, type SearchArea } from '@/lib/areaSearch';
import { WARSAW_MSI_REJONY } from '@/data/warsawMsiRejony';

/**
 * Model zbiorów dla wyboru obszaru (decyzja Piotra 2026-09-26).
 *
 * Trzy reguły, wszystkie o jednym: **wybrane obszary mają się sumować, ale nigdy nie
 * zawierać jeden drugiego.** Dzielnica i jej osiedle to nie dwa obszary, tylko ten sam
 * obszar opisany dwa razy — a dwa opisy tego samego dają filtr, który nic nie zawęża i
 * licznik, który kłamie.
 *
 *   1. Wybór OSIEDLA zawęża dzielnicę: dodanie „Stary Mokotów" usuwa „Mokotów" z wyboru,
 *      bo człowiek właśnie powiedział, że nie chce całej dzielnicy.
 *   2. Wybór DZIELNICY pochłania jej osiedla: dodanie „Mokotów", gdy wybrany jest „Stary
 *      Mokotów", usuwa to osiedle. To jest ta sama reguła w drugą stronę i musi tu być,
 *      inaczej po dwóch dotknięciach zostaje para, której reguła 1 zabrania.
 *   3. Osiedla z RÓŻNYCH dzielnic się sumują, a stacja dokłada się do sumy — stacja to
 *      promień wokół punktu, nie część dzielnicy, więc niczego nie zawęża ani nie pochłania.
 *   4. Miejsce („Blisko do…”, `kind: 'place'`) jest najwyżej jedno: nowe zastępuje poprzednie
 *      (plan §1.18; od §1.90 miejsce wybiera się też z głównego pola).
 *
 * Limit ośmiu obszarów: przy dziewiątym po prostu nie dodajemy. Odrzucenie jest
 * przewidywalne; wypychanie najstarszego znaczyłoby, że dotknięcie chipa po cichu usuwa
 * inny, wybrany wcześniej.
 */
export const MAX_OBSZAROW = 8;

const dzielnicaOsiedla = new Map(WARSAW_MSI_REJONY.map((r) => [r.name, r.district]));

/** Dzielnica, do której należy obszar — albo null, gdy pojęcie nie ma zastosowania. */
export function dzielnicaObszaru(a: SearchArea): string | null {
  if (a.kind === 'district') return a.name;
  if (a.kind === 'subdistrict') return dzielnicaOsiedla.get(a.name) ?? null;
  return null;
}

export interface WynikDodania {
  areas: SearchArea[];
  /** true, gdy nic nie dodano, bo lista jest pełna — UI może o tym powiedzieć. */
  limitOsiagniety: boolean;
  /**
   * Klucze obszarów, które nowy zastąpił (dzielnica za osiedle albo osiedla za dzielnicę). Pole
   * wyszukiwania przygasza je przy najechaniu na podpowiedź, zanim się ją wybierze (prototyp 19.10).
   */
  zastapione: string[];
}

export function dodajObszar(areas: SearchArea[], nowy: SearchArea): WynikDodania {
  const klucz = areaKey(nowy);
  if (areas.some((a) => areaKey(a) === klucz)) return { areas, limitOsiagniety: false, zastapione: [] };

  let zastapione: string[] = [];
  if (nowy.kind === 'subdistrict') {
    // Reguła 1: osiedle zawęża swoją dzielnicę.
    const rodzic = dzielnicaObszaru(nowy);
    if (rodzic) zastapione = areas.filter((a) => a.kind === 'district' && a.name === rodzic).map(areaKey);
  } else if (nowy.kind === 'district') {
    // Reguła 2: dzielnica pochłania swoje osiedla.
    zastapione = areas.filter((a) => a.kind === 'subdistrict' && dzielnicaObszaru(a) === nowy.name).map(areaKey);
  } else if (nowy.kind === 'place') {
    // Reguła 4: miejsce jest JEDNO (plan §1.18) — nowe zastępuje poprzednie, tak jak w „Blisko
    // do…” (`zJednymMiejscem`). Tu przez zastąpienie w miejscu, więc chip świeci jak przy osiedlu.
    zastapione = areas.filter((a) => a.kind === 'place').map(areaKey);
  }
  const bez = areas.filter((a) => !zastapione.includes(areaKey(a)));

  // „Blisko do…” (miejsce, A9) nie zajmuje miejsca w limicie: limit liczy dzielnice, osiedla i
  // stacje (makieta panelu: `areaCount = districts + estates`), a miejsce jest jedno, osobno.
  // Limit blokuje tylko DOPISANIE. Zastąpienie listy nie wydłuża, więc przechodzi zawsze — także
  // przy liście dłuższej niż 8 z czasów przed limitem (onboarding zapisywał dowolnie wiele
  // dzielnic; audyt 19.10: wtedy „Stary Mokotów” w miejsce „Mokotowa” był odrzucany).
  if (nowy.kind !== 'place' && !zastapione.length && bez.filter((a) => a.kind !== 'place').length >= MAX_OBSZAROW) {
    return { areas, limitOsiagniety: true, zastapione: [] };
  }
  if (!zastapione.length) return { areas: [...bez, nowy], limitOsiagniety: false, zastapione };

  // ZASTĄPIENIE W MIEJSCU (prototyp Maćka 19.10): nowy obszar staje tam, gdzie stał pierwszy
  // zastąpiony, a nie na końcu — chip „Nowy Rembertów · Rembertów” zajmuje miejsce „Rembertowa”,
  // więc widać, co się stało, bez komunikatu i bez „Cofnij”.
  const pierwszy = areas.findIndex((a) => zastapione.includes(areaKey(a)));
  const wynik: SearchArea[] = [];
  areas.forEach((a, i) => {
    if (i === pierwszy) wynik.push(nowy);
    if (!zastapione.includes(areaKey(a))) wynik.push(a);
  });
  return { areas: wynik, limitOsiagniety: false, zastapione };
}

export function usunObszar(areas: SearchArea[], klucz: string): SearchArea[] {
  return areas.filter((a) => areaKey(a) !== klucz);
}

/**
 * Porządkuje listę wczytaną z zewnątrz (adres, „Moje filtry”, historia) wg reguł zawierania:
 * para dzielnica + jej osiedle zostaje sprowadzona do tego, co wybrano później, jak przy klikaniu.
 * Limitu 8 tu NIE stosuje — przycinanie zapisanego zestawu po cichu gubiłoby obszary; limit
 * pilnuje tylko dopisywania. Audyt 19.10: do 19.10 pole na pulpicie i mapa omijały reguły, więc
 * zapisane zestawy mogą mieć pary „Rembertów + Nowy Rembertów”.
 */
export function uporzadkujObszary(areas: SearchArea[]): SearchArea[] {
  let wynik: SearchArea[] = [];
  for (const a of areas) {
    const klucz = areaKey(a);
    if (wynik.some((b) => areaKey(b) === klucz)) continue;
    let zastapione: string[] = [];
    if (a.kind === 'subdistrict') {
      const rodzic = dzielnicaObszaru(a);
      zastapione = wynik.filter((b) => b.kind === 'district' && b.name === rodzic).map(areaKey);
    } else if (a.kind === 'district') {
      zastapione = wynik.filter((b) => b.kind === 'subdistrict' && dzielnicaObszaru(b) === a.name).map(areaKey);
    }
    if (!zastapione.length) {
      wynik = [...wynik, a];
      continue;
    }
    const pierwszy = wynik.findIndex((b) => zastapione.includes(areaKey(b)));
    const nowa: SearchArea[] = [];
    wynik.forEach((b, i) => {
      if (i === pierwszy) nowa.push(a);
      if (!zastapione.includes(areaKey(b))) nowa.push(b);
    });
    wynik = nowa;
  }
  return wynik;
}
